'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import BenefitIcon from '@/fo/icons/base/doctor/benefit.svg';
import PhotoIcon from '@/fo/icons/base/doctor/photo.svg';
import ArrowIcon from '@/fo/icons/base/icons/arrow24.svg';
import { UiDoctorSelectorCity } from '@/fo/ui/doctor/selector/city';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitPaginationBase } from '@/fo/ui/kit/pagination/base';
import { LISTING_PAGE_PARAM } from '@/fo/util/listing-page';
import { utilPluralize } from '@/fo/util/pluralize';

import { Appointment } from '../components/Appointment/Appointment';
import { Doctor, Clinic } from '../types';

import { styles } from './UiDoctorListBase.styles';

export interface UiDoctorListBaseProps {
  doctors: Doctor[];
  onAddressClick?: (doctor: Doctor, clinic: Clinic) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
  };
  onPageChange?: (page: number) => void;
  onLoadMore?: () => void;
  loading?: boolean;
  bothers?: Array<{ id: string; label: string }>;
}

export function UiDoctorListBase({
  doctors,
  onAddressClick,
  pagination,
  onPageChange,
  onLoadMore,
  loading = false,
  bothers = []
}: UiDoctorListBaseProps) {
  const slots = styles();
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Doctor | null>(null);

  const pluralizeYears = utilPluralize({
    one: t('pluralize.years.one'),
    few: t('pluralize.years.few'),
    many: t('pluralize.years.many')
  });
  const currentYear = new Date().getFullYear();

  const getBotherLabels = (codes: string[] | null | undefined) => {
    if (!codes) return null;

    const labelsById = Object.fromEntries(
      bothers.map((item) => [item.id, item.label])
    );
    return codes.map((code) => labelsById[code] || code);
  };

  const onCardClick = (doctor: Doctor) => {
    if (!doctor.clinics || doctor.clinics.length === 0) return;

    setSelected(doctor);
    setOpen(true);
  };

  const handlePageChange = (page: number) => {
    onPageChange?.(page);
  };

  const handleLoadMore = () => {
    onLoadMore?.();
  };

  const getPageHref = (targetPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete('page');
    const n = targetPage >= 1 ? targetPage : 1;
    if (n <= 1) {
      params.delete(LISTING_PAGE_PARAM);
    } else {
      params.set(LISTING_PAGE_PARAM, String(n));
    }

    const query = params.toString();

    return query ? `${pathname}?${query}` : pathname;
  };

  return (
    <>
      <div className={slots.base()}>
        {doctors.length > 0 &&
          doctors.map((doctor: Doctor, index: number) => {
            return (
              <div
                className={slots.card()}
                key={`${doctor.full_name}-${doctor.url_key}-${index}`}
              >
                <Link
                  href={doctor.url_key ? `/specialists/${doctor.url_key}` : '#'}
                  className={slots.top()}
                >
                  <div className={slots.photo()}>
                    {doctor.photo ? (
                      <Image
                        src={doctor.photo}
                        height={120}
                        width={120}
                        alt={doctor.photo_alt || ''}
                      />
                    ) : (
                      <PhotoIcon className={slots.photoIcon()} />
                    )}
                  </div>
                  <div className={slots.info()}>
                    {getBotherLabels(doctor.bother) && (
                      <div className={slots.labelsDesktop()}>
                        {getBotherLabels(doctor.bother)?.map(
                          (label: string) => (
                            <div
                              className={slots.label()}
                              key={label}
                            >
                              {label}
                            </div>
                          )
                        )}
                      </div>
                    )}
                    <div className={slots.name()}>{doctor.full_name}</div>
                    <div className={slots.desktopInfo()}>
                      <p
                        className={slots.subtitle()}
                        dangerouslySetInnerHTML={{
                          __html: doctor.regalia || ''
                        }}
                      />
                      <div className={slots.experience()}>
                        {!!doctor.qualification?.length && (
                          <span>
                            {doctor.qualification
                              ?.map((item) => item)
                              .join(', ')}
                          </span>
                        )}
                        {doctor.seniority && (
                          <span>
                            {' '}
                            / {t('seniority')}{' '}
                            {currentYear - Number(doctor.seniority)}{' '}
                            {pluralizeYears(
                              currentYear - Number(doctor.seniority)
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
                <div className={slots.bottom()}>
                  <div className={slots.mobileInfo()}>
                    <p
                      className={slots.subtitle()}
                      dangerouslySetInnerHTML={{
                        __html: doctor.regalia || ''
                      }}
                    />
                    <div className={slots.experience()}>
                      {doctor.qualification && (
                        <span>{doctor.qualification}</span>
                      )}
                      {doctor.seniority && (
                        <span>
                          {' '}
                          / {t('seniority')}{' '}
                          {currentYear - Number(doctor.seniority)}{' '}
                          {pluralizeYears(
                            currentYear - Number(doctor.seniority)
                          )}
                        </span>
                      )}
                    </div>
                    {getBotherLabels(doctor.bother) && (
                      <div className={slots.labelsMobile()}>
                        {getBotherLabels(doctor.bother)?.map(
                          (label: string) => (
                            <div
                              className={slots.label()}
                              key={label}
                            >
                              {label}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                  {doctor.clinics && doctor.clinics.length > 0 && (
                    <UiDoctorSelectorCity
                      city={doctor.city || ''}
                      size="xs"
                      className={slots.city()}
                    />
                  )}
                  {doctor.clinics && (
                    <div className={slots.addresses()}>
                      {doctor.clinics.map((item, j) => (
                        <div
                          className={slots.address()}
                          key={(item.name || 'clinic') + j}
                          onClick={() =>
                            onAddressClick && item.latitude && item.longitude
                              ? onAddressClick(doctor, item)
                              : undefined
                          }
                          style={{
                            cursor:
                              onAddressClick && item.latitude && item.longitude
                                ? 'pointer'
                                : 'default'
                          }}
                        >
                          <span className={slots.addressIcon()}>
                            <BenefitIcon />
                          </span>
                          <span>
                            {item.name && <span>{`${item.name}, `}</span>}
                            {item.address && <span>{item.address}</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {doctor.clinics && (
                    <UiKitButtonBase
                      color="ghost"
                      className={slots.button()}
                      onClick={() => onCardClick(doctor)}
                    >
                      <div className={slots.buttonContent()}>
                        {t('Make an appointment')}
                        <ArrowIcon />
                      </div>
                    </UiKitButtonBase>
                  )}
                </div>
              </div>
            );
          })}
        {doctors.length === 0 && !loading && (
          <p className={slots.empty()}>{t('No doctors found')}</p>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className={slots.pagination()}>
          <UiKitPaginationBase
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            onLoadMore={handleLoadMore}
            getPageHref={getPageHref}
            disabledNavigation
            disabledMore={!onLoadMore}
          />
        </div>
      )}
      <UiKitDrawerBase
        title={t('Make an appointment')}
        open={open}
        onOpenChange={setOpen}
        direction="right"
        withoutMobile
      >
        {selected && <Appointment doctor={selected} />}
      </UiKitDrawerBase>
    </>
  );
}
